/**
 * Kostenverteilung: anteilig je Spieler je Stunde vs. je Saison/Serie.
 * Abrechnungsrhythmus (monatlich, Voraus/Nach) bleibt bei billing_frequency / billing_payment_timing.
 */

export const BILLING_ALLOCATION_HOURLY = "hourly";
export const BILLING_ALLOCATION_SEASON = "season";

/** @deprecated Alias */
export const BILLING_SPLIT_MODE_ATTENDANCE = BILLING_ALLOCATION_HOURLY;
/** @deprecated Alias */
export const BILLING_SPLIT_MODE_FLAT_PLANNED = BILLING_ALLOCATION_SEASON;

export const BILLING_ALLOCATION_MODE_IDS = Object.freeze([
  BILLING_ALLOCATION_HOURLY,
  BILLING_ALLOCATION_SEASON,
]);

const LEGACY_ALLOCATION = Object.freeze({
  attendance_based: BILLING_ALLOCATION_HOURLY,
  flat_planned: BILLING_ALLOCATION_SEASON,
});

function nz(v) {
  return String(v ?? "").trim();
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

export function normalizeBillingAllocationMode(
  value,
  fallback = BILLING_ALLOCATION_HOURLY,
) {
  const v = nz(value);
  if (BILLING_ALLOCATION_MODE_IDS.includes(v)) return v;
  return LEGACY_ALLOCATION[v] || fallback;
}

/** @deprecated */
export const normalizeBillingSplitMode = normalizeBillingAllocationMode;

function readAllocationFromRow(row) {
  const r = row && typeof row === "object" ? row : {};
  return normalizeBillingAllocationMode(
    r.billing_allocation_mode ??
      r.billingAllocationMode ??
      r.billing_split_mode ??
      r.billingSplitMode,
  );
}

/**
 * Effektive Verteilung: Session → Serien-Override → Trainer-Default.
 */
export function resolveSessionBillingAllocationConfig(session, ctx = {}) {
  const s = session && typeof session === "object" ? session : {};
  const trainer =
    ctx.trainer && typeof ctx.trainer === "object" ? ctx.trainer : {};
  const seriesBilling =
    ctx.seriesBilling && typeof ctx.seriesBilling === "object"
      ? ctx.seriesBilling
      : null;

  const seriesId = nz(s.series_id ?? s.seriesId);
  const useSeries =
    seriesId &&
    seriesBilling &&
    nz(seriesBilling.series_id) === seriesId &&
    seriesBilling.use_trainer_defaults !== true;

  const billingAllocationMode = useSeries
    ? readAllocationFromRow(seriesBilling)
    : readAllocationFromRow({ ...trainer, ...s });

  return {
    billingAllocationMode,
    seriesBilling: useSeries ? seriesBilling : null,
  };
}

export function isSeasonAllocationBilling(session, ctx = {}) {
  return (
    resolveSessionBillingAllocationConfig(session, ctx)
      .billingAllocationMode === BILLING_ALLOCATION_SEASON
  );
}

/** @deprecated */
export const isFlatPlannedBilling = isSeasonAllocationBilling;

function resolveBillingPaymentTiming(session, ctx = {}) {
  const s = session && typeof session === "object" ? session : {};
  const trainer =
    ctx.trainer && typeof ctx.trainer === "object" ? ctx.trainer : {};
  const seriesBilling =
    ctx.seriesBilling && typeof ctx.seriesBilling === "object"
      ? ctx.seriesBilling
      : null;
  return (
    nz(ctx.billingPaymentTiming) ||
    nz(
      seriesBilling?.billing_payment_timing ??
        seriesBilling?.billingPaymentTiming,
    ) ||
    nz(s.billing_payment_timing ?? s.billingPaymentTiming) ||
    nz(trainer.billing_payment_timing) ||
    "arrears"
  );
}

/** Saison-Pauschale mit Vorauszahlung (Rhythmus-Feld billing_payment_timing=advance). */
export function isSeasonAdvanceLumpSum(session, ctx = {}) {
  if (!isSeasonAllocationBilling(session, ctx)) return false;
  return resolveBillingPaymentTiming(session, ctx) === "advance";
}

/** @deprecated */
export const isSeriesPrepaidBilling = isSeasonAdvanceLumpSum;

/**
 * Nach Einmal-Rechnung für Saison-Vorauszahlung: keine Session-Positionen mehr sammeln.
 */
export function shouldSkipSessionForPeriodicBilling(session, ctx = {}) {
  if (!isSeasonAdvanceLumpSum(session, ctx)) return false;
  return (
    session?.series_prepaid_billed === true ||
    session?.seriesPrepaidBilled === true
  );
}

export function resolveSplitBaseForBilling(session, ctx = {}) {
  if (isSeasonAllocationBilling(session, ctx)) return "planned";
  return nz(session?.split_base ?? session?.splitBase) === "present"
    ? "present"
    : "planned";
}

/**
 * Session-Felder aus Trainer + optional Serien-Konfiguration.
 */
export function buildSessionBillingSplitPersistFields(trainer, options = {}) {
  const tr = trainer && typeof trainer === "object" ? trainer : {};
  const seriesBilling =
    options.seriesBilling && typeof options.seriesBilling === "object"
      ? options.seriesBilling
      : null;
  const useSeriesOverride =
    seriesBilling && seriesBilling.use_trainer_defaults !== true;

  const billingAllocationMode = normalizeBillingAllocationMode(
    useSeriesOverride
      ? readAllocationFromRow(seriesBilling)
      : (options.billingAllocationMode ?? readAllocationFromRow(tr)),
  );

  const splitBase =
    billingAllocationMode === BILLING_ALLOCATION_SEASON ? "planned" : "present";

  const fields = {
    billing_allocation_mode: billingAllocationMode,
    split_base: splitBase,
  };

  if (billingAllocationMode === BILLING_ALLOCATION_SEASON && seriesBilling) {
    const total = computeSeriesTotalEur(seriesBilling, options);
    const units = Math.max(
      1,
      Number(seriesBilling.session_units ?? options.sessionCount) || 1,
    );
    if (total > 0) {
      fields.pricing_type = "fixed_total";
      fields.fixed_total_eur = round2(total / units);
    }
  }

  return fields;
}

export function computeSeriesTotalEur(seriesBilling, options = {}) {
  const sb =
    seriesBilling && typeof seriesBilling === "object" ? seriesBilling : {};
  const explicit = Number(sb.series_total_eur ?? sb.seriesTotalEur);
  if (Number.isFinite(explicit) && explicit > 0) return round2(explicit);

  const rate = Number(
    sb.hourly_rate_eur ?? sb.hourlyRateEur ?? options.hourlyRateEur,
  );
  const units = Math.max(
    1,
    Number(sb.session_units ?? sb.sessionUnits ?? options.sessionCount) || 0,
  );
  const durationMin = Number(options.durationMin) || 60;
  if (Number.isFinite(rate) && rate > 0 && units > 0) {
    return round2((durationMin / 60) * rate * units);
  }
  return 0;
}

/** Anteil je Spieler für die ganze Saison/Serie. */
export function personShareFromSeasonAllocation(
  personId,
  seriesBilling,
  plannedIds = [],
) {
  const ids = Array.isArray(plannedIds)
    ? plannedIds
        .map((x) => nz(typeof x === "object" && x?.id != null ? x.id : x))
        .filter(Boolean)
    : [];
  const pid = nz(personId);
  if (!ids.includes(pid)) {
    return {
      shareEur: 0,
      pricingType: "season_allocation",
      payerCount: ids.length || 1,
    };
  }
  const total = computeSeriesTotalEur(seriesBilling);
  const n = Math.max(ids.length, 1);
  return {
    shareEur: round2(total / n),
    pricingType: "season_allocation",
    payerCount: n,
    seriesTotalEur: total,
  };
}

/** @deprecated */
export const personShareFromSeriesPrepaid = personShareFromSeasonAllocation;

export function buildSeriesBillingRowInput({
  seriesId,
  tenantId,
  trainerId,
  trainer,
  seriesBillingForm = {},
  sessionCount = 0,
  hourlyRateEur = null,
  durationMin = 60,
}) {
  const useTrainerDefaults = seriesBillingForm.use_trainer_defaults === true;
  const billingAllocationMode = normalizeBillingAllocationMode(
    useTrainerDefaults
      ? readAllocationFromRow(trainer)
      : readAllocationFromRow({
          ...trainer,
          billing_allocation_mode: seriesBillingForm.billing_allocation_mode,
        }),
  );

  const sessionUnits = Math.max(
    1,
    Number(seriesBillingForm.session_units ?? sessionCount) ||
      sessionCount ||
      1,
  );
  const rate =
    Number(seriesBillingForm.hourly_rate_eur ?? hourlyRateEur) ||
    Number(hourlyRateEur) ||
    null;

  let seriesTotalEur = Number(seriesBillingForm.series_total_eur);
  if (!Number.isFinite(seriesTotalEur) || seriesTotalEur <= 0) {
    seriesTotalEur = computeSeriesTotalEur(
      { hourly_rate_eur: rate, session_units: sessionUnits },
      { durationMin, sessionCount: sessionUnits, hourlyRateEur: rate },
    );
  }

  const billingPaymentTiming = nz(seriesBillingForm.billing_payment_timing);
  const row = {
    series_id: nz(seriesId),
    tenant_id: tenantId,
    trainer_id: nz(trainerId),
    billing_allocation_mode: billingAllocationMode,
    series_total_eur: seriesTotalEur > 0 ? round2(seriesTotalEur) : null,
    hourly_rate_eur: rate,
    session_units: sessionUnits,
    use_trainer_defaults: useTrainerDefaults,
  };
  if (billingPaymentTiming) {
    row.billing_payment_timing = billingPaymentTiming;
  }
  return row;
}
